import * as React from "react";
import styled, { css } from "styled-components";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { getValueFromInput } from "util/FormsUtils";
import { ConfigurationsActions } from "stores/configurations/ConfigurationsStore";
import { ConfigurationType } from "components/configurations/ConfigurationTypes";
import { Alert, Table, Modal } from "components/bootstrap";
import { FormikInput, ModalSubmit, SortableList } from "components/common";
import * as ISODurationUtils from "util/ISODurationUtils";
import MessageProcessorStatusFormGroup from "components/configurations/message-processors/MessageProcessorStatusFormGroup";
import useProductName from "brand-customization/useProductName";
const DEFAULT_GRACE_PERIOD = "P2D";
const LabelSpan = styled.span(
  ({ theme }) => css`
    margin-left: ${theme.spacings.sm};
    font-weight: bold;
  `
);
const ProcessingConfigModalForm = ({ closeModal, formConfig }) => {
  const { t } = useTranslation("configurations");
  const productName = useProductName();
  const hasNoActiveProcessor = () => formConfig.disabled_processors.length >= formConfig.processor_order.length;
  const saveConfig = (values) => {
    if (!hasNoActiveProcessor()) {
      const { processor_order, disabled_processors, grace_period } = values;
      Promise.allSettled([
        ConfigurationsActions.updateMessageProcessorsConfig(ConfigurationType.MESSAGE_PROCESSORS_CONFIG, {
          processor_order,
          disabled_processors
        }),
        ConfigurationsActions.update(ConfigurationType.GLOBAL_PROCESSING_RULE_CONFIG, { grace_period })
      ]).then(() => {
        closeModal();
      });
    }
  };
  const updateSorting = (newSorting, setFieldValue) => {
    const processorOrder = newSorting.map((item) => ({ class_name: item.id, name: item.title }));
    setFieldValue("processor_order", processorOrder);
  };
  const sortableItems = (formValues) => formValues.processor_order.map((processor) => ({ id: processor.class_name, title: processor.name }));
  const handleEnableFutureTimestampNormalisation = (enabled, setFieldValue) => {
    if (enabled) {
      setFieldValue("grace_period", (formConfig == null ? void 0 : formConfig.grace_period) || DEFAULT_GRACE_PERIOD);
    } else {
      setFieldValue("grace_period", void 0);
    }
  };
  const gracePeriodValidator = (milliseconds) => milliseconds >= 60 * 1e3;
  const validateGracePeriodField = (enabled) => (value) => {
    let errorMessage = "";
    if (enabled && !ISODurationUtils.isValidDuration(value, gracePeriodValidator)) {
      errorMessage = t("message_processors.grace_period_invalid");
    }
    return errorMessage;
  };
  return /* @__PURE__ */ React.createElement(Modal, { show: true, "data-testid": "config-modal", onHide: closeModal }, /* @__PURE__ */ React.createElement(Formik, { onSubmit: saveConfig, initialValues: formConfig }, ({ isSubmitting, values, setFieldValue, isValid }) => /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, t("message_processors.update_title"))), /* @__PURE__ */ React.createElement(Modal.Body, null, /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("h2", null, t("message_processors.global_title")), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "enableFutureTimestampNormalization",
      id: "enableFutureTimestampNormalization",
      help: t("message_processors.future_timestamp_help", { productName }),
      onChange: (event) => handleEnableFutureTimestampNormalisation(Boolean(getValueFromInput(event.target)), setFieldValue),
      label: /* @__PURE__ */ React.createElement(LabelSpan, null, t("message_processors.future_timestamp_normalization"))
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "text",
      name: "grace_period",
      id: "grace_period",
      placeholder: DEFAULT_GRACE_PERIOD,
      label: t("message_processors.grace_period"),
      disabled: !(values == null ? void 0 : values.enableFutureTimestampNormalization),
      help: t("message_processors.grace_period_help", { productName }),
      addonAfter: values.enableFutureTimestampNormalization && ISODurationUtils.formatDuration(values.grace_period, gracePeriodValidator, t("shared.invalid")),
      validate: validateGracePeriodField(values.enableFutureTimestampNormalization),
      required: true
    }
  ), /* @__PURE__ */ React.createElement("h2", null, t("message_processors.title")), /* @__PURE__ */ React.createElement("h3", null, t("message_processors.order")), /* @__PURE__ */ React.createElement("p", null, t("message_processors.order_help")), /* @__PURE__ */ React.createElement(
    SortableList,
    {
      items: sortableItems(values),
      onMoveItem: (newSorting) => updateSorting(newSorting, setFieldValue),
      displayOverlayInPortal: true
    }
  ), /* @__PURE__ */ React.createElement("h3", null, t("message_processors.status")), /* @__PURE__ */ React.createElement("p", null, t("message_processors.status_help")), /* @__PURE__ */ React.createElement(Table, { bordered: true, condensed: true, className: "top-margin" }, /* @__PURE__ */ React.createElement("thead", null, /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("th", null, t("message_processors.processor")), /* @__PURE__ */ React.createElement("th", null, t("message_processors.enabled")))), /* @__PURE__ */ React.createElement("tbody", null, /* @__PURE__ */ React.createElement(MessageProcessorStatusFormGroup, null))), hasNoActiveProcessor() && /* @__PURE__ */ React.createElement(Alert, { bsStyle: "danger", title: t("shared.error") }, t("message_processors.no_active_processor")))), /* @__PURE__ */ React.createElement(Modal.Footer, null, /* @__PURE__ */ React.createElement(
    ModalSubmit,
    {
      onCancel: closeModal,
      isSubmitting,
      disabledSubmit: !isValid,
      isAsyncSubmit: true,
      submitLoadingText: t("shared.update_configuration"),
      submitButtonText: t("shared.update_configuration")
    }
  )))));
};
export default ProcessingConfigModalForm;
